import {
    QueryClient,
    defaultShouldDehydrateQuery,
} from '@tanstack/react-query';
import {
    PersistQueryClientProviderProps,
    PersistedClient,
    Persister,
} from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';
import { isEmpty } from 'lodash';

const CACHE_MAX_AGE = 1000 * 60 * 10; // 10 minutes

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            gcTime: CACHE_MAX_AGE,
        },
    },
});

/**
 * Creates an Indexed DB persister
 * @see
 * - https://tanstack.com/query/v4/docs/framework/react/plugins/persistQueryClient#building-a-persister
 * - https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API
 */
function createIDBPersister(idbValidKey: IDBValidKey = 'reactQuery') {
    return {
        persistClient: async (client: PersistedClient) => {
            try {
                await set(idbValidKey, client);
            } catch (err: any) {
                const state = !isEmpty(client.clientState.queries[0]) ? client.clientState.queries[0].state : null;
                const { data = null, error = null, status = null } = state || {};

                console.error('Persisting React Query client failed', {
                    data,
                    error,
                    status,
                    originalError: err.message,
                });
            }
        },
        restoreClient: async () => {
            return await get<PersistedClient>(idbValidKey);
        },
        removeClient: async () => {
            await del(idbValidKey);
        },
    } as Persister;
}

export const persistOptions: PersistQueryClientProviderProps['persistOptions'] =
    {
        maxAge: CACHE_MAX_AGE,
        persister: createIDBPersister(),
        dehydrateOptions: {
            shouldDehydrateQuery: (query) => {
                // Only persist certain queries
                // Since we're overwriting the default behaviour, we need to manually call the default implementation too
                return (
                    defaultShouldDehydrateQuery(query) && !!query.meta?.persist
                );
            },
        },
    };
